import { RECOMMENDATIONS_V2 } from 'constants/recommendationServiceV2Flags';
import { isFlagEnabled } from './launchDarkly';

/**
 * Routing helpers for the Recommendation Service V2 (Java -> Node) migration.
 *
 * Every helper resolves the backend when it is called, not when it is imported, so
 * the axios resources in `utilities/api` can be built once at module load and still
 * send each request to whichever backend `releaseBeRecommendationsV2` selects.
 */

/** True when recommendation requests should go to the Node service. */
export const isRecommendationsV2Enabled = (): boolean =>
  isFlagEnabled(RECOMMENDATIONS_V2);

/**
 * Java uses the singular `recommendation` for every operation except the list; Node
 * uses the plural `recommendations` for all of them.
 */
export const RECOMMENDATION_V2_RESOURCE_NAME = 'recommendations';

/**
 * Paths with no Node counterpart, which stay on Java even while the flag is on.
 *
 * `recommendation/{id}/task` is deprecated and will be retired together with the Java
 * services rather than ported, so routing it to Node would only 404.
 *
 * `recommendation/{id}/linked-records` is Java-only for a different reason — it landed
 * after the Node port inventory was taken and has not been ported yet — but it never
 * reaches this seam, because `screens/Recommendations/hooks/useRecommendationLinkedRecords`
 * builds it with raw axios. It needs no entry here until it moves onto these resources.
 */
const JAVA_ONLY_ACTIONS = [/^recommendations?\/[^/]+\/task$/];

export const isJavaOnlyRecommendationAction = (action?: string): boolean =>
  !!action && JAVA_ONLY_ACTIONS.some(pattern => pattern.test(action));

/**
 * Rewrites a `createChildApi` action from the Java path to the Node path, e.g.
 * `recommendation/{id}` -> `recommendations/{id}`. Idempotent: an action already
 * using the plural form is returned unchanged.
 */
export const remapRecommendationAction = (action: string): string =>
  action.replace(/^recommendation(?=\/|$)/, 'recommendations');
